// KeyFailureBanner — shows in-app when one of the user's keys (LLM BYOK,
// the platform Stripe key for super admins) has thrown an error in the last
// 24 hours, OR when a visitor reported a creator's silo is down. Backend
// writes the row in llm_failure_alerts; this surface makes sure the user
// sees it the next time they open the app even if they missed the email.
//
// Jun 5, 2026 — copy is now TAUGHT by `kind`:
//   invalid_key   → the key string is rejected. Topping up does nothing —
//                   paste a fresh/valid key.
//   quota         → the key is fine, just out of allowance/funds — top up.
//   visitor_report→ a fan hit a wall on the creator's silo.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Key, ArrowSquareOut, Megaphone, WarningCircle, ShieldWarning } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PROVIDER_LABELS = {
  anthropic: "Anthropic",
  openai: "OpenAI",
  gemini: "Google Gemini",
  stripe: "Stripe",
};

const PROVIDER_CONSOLES = {
  anthropic: "https://console.anthropic.com/settings/keys",
  openai: "https://platform.openai.com/api-keys",
  gemini: "https://aistudio.google.com/app/apikey",
  stripe: "https://dashboard.stripe.com/test/apikeys",
};

export default function KeyFailureBanner() {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const nav = useNavigate();

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get("/me/key-failure-alerts");
        if (mounted) setAlerts(r.data.alerts || []);
      } catch {
        // Silent fail — banner is opportunistic
      }
    };
    load();
    const id = setInterval(load, 60_000); // refresh every minute
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const visible = alerts.filter((a) => !dismissed.has(a.provider));
  if (visible.length === 0) return null;

  const handleDismiss = async (provider) => {
    setDismissed((s) => new Set(s).add(provider));
    try {
      await api.post("/me/key-failure-alerts/dismiss", { provider });
    } catch {
      // Re-add on failure
      setDismissed((s) => { const n = new Set(s); n.delete(provider); return n; });
    }
  };

  // Returns the teaching content for one alert row.
  const contentFor = (a) => {
    const label = PROVIDER_LABELS[a.provider] || a.provider;
    const console_url = PROVIDER_CONSOLES[a.provider] || "";
    const isStripe = a.is_stripe || a.provider === "stripe";

    if (a.provider === "visitor_report") {
      return {
        icon: <Megaphone size={22} weight="fill" />,
        title: "A visitor hit a wall on your silo",
        body: (
          <>Someone tried to ask your silo a question, but its AI engine didn't answer —
          almost always an <strong>invalid key</strong> or empty funding. Re-check your AI key
          below so fans can get answers again.</>
        ),
        primary: { label: "Check my AI key →", onClick: () => nav("/account-settings?focus=byok") },
        link: null,
      };
    }

    if (isStripe) {
      return {
        icon: <ShieldWarning size={22} weight="fill" />,
        title: `${label} key is failing platform-wide`,
        body: (
          <>Every payment route is broken right now. Rotate the secret key in your Stripe Dashboard,
          then paste it into <strong>Super Admin → Stripe Settings</strong>.</>
        ),
        primary: null,
        link: console_url ? { label: `Open ${label} dashboard →`, href: console_url } : null,
      };
    }

    if (a.kind === "invalid_key") {
      return {
        icon: <Key size={22} weight="fill" />,
        title: `Your ${label} key is being rejected`,
        body: (
          <>{label} says this key is <strong>invalid or expired</strong> — so this is
          <strong> not</strong> a balance problem, and <strong>topping up won't fix it</strong>.
          Open {label}, renew or generate a fresh key (make sure it's active and unrestricted),
          then paste the new key in your settings. This banner clears itself once a working key
          answers a question.</>
        ),
        primary: { label: "Paste a new key →", onClick: () => nav("/account-settings?focus=byok") },
        link: console_url ? { label: `Renew your key at ${label} →`, href: console_url } : null,
      };
    }

    if (a.kind === "quota") {
      return {
        icon: <WarningCircle size={22} weight="fill" />,
        title: `Your ${label} key hit its limit`,
        body: (
          <>Good news — your key is fine. You've hit your {label} usage or billing limit. Top up your
          balance (or wait for the reset) and you're back in business — no need to make a new key.</>
        ),
        primary: null,
        link: console_url ? { label: `Open ${label} billing →`, href: console_url } : null,
      };
    }

    // auth_other / unknown
    return {
      icon: <WarningCircle size={22} weight="fill" />,
      title: `Your ${label} key just failed`,
      body: (
        <>{label} rejected your last request. If you recently changed or deleted this key, paste a
        fresh one. If your account ran out of funds, top up at {label}.</>
      ),
      primary: { label: "Re-check my key →", onClick: () => nav("/account-settings?focus=byok") },
      link: console_url ? { label: `Open ${label} →`, href: console_url } : null,
    };
  };

  return (
    <div className="space-y-2 mb-4" data-testid="key-failure-banner-stack">
      {visible.map((a) => {
        const c = contentFor(a);
        return (
          <div
            key={a.provider}
            className="border-2 border-[#C16E5A] bg-[#FFF4F0] rounded-lg p-4 flex gap-3 items-start"
            data-testid={`key-failure-banner-${a.provider}`}
          >
            <div className="text-[#C16E5A] flex-shrink-0 mt-0.5">{c.icon}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#7A3A2A]" data-testid={`key-failure-title-${a.provider}`}>
                {c.title}
              </div>
              <div className="text-xs text-[#5C3A2A] mt-1 leading-relaxed">
                {c.body}
                {a.alert_count > 1 && (
                  <span className="text-[10px] text-[#7A3A2A] block mt-1">
                    ({a.alert_count} failures in the last 24h — it'll keep returning until the key works)
                  </span>
                )}
              </div>
              <div className="mt-2 flex gap-2 flex-wrap items-center">
                {c.primary && (
                  <button
                    onClick={c.primary.onClick}
                    className="text-xs px-3 py-1.5 bg-[#7A3A2A] text-white rounded font-semibold hover:bg-[#5C2A1F] inline-flex items-center gap-1"
                    data-testid={`key-failure-primary-${a.provider}`}
                  >
                    <Key size={12} weight="bold" /> {c.primary.label}
                  </button>
                )}
                {c.link && (
                  <a
                    href={c.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 bg-white border border-[#C16E5A] text-[#7A3A2A] rounded font-semibold hover:bg-[#FFF4F0] inline-flex items-center gap-1"
                    data-testid={`key-failure-link-${a.provider}`}
                  >
                    {c.link.label} <ArrowSquareOut size={11} />
                  </a>
                )}
                <button
                  onClick={() => handleDismiss(a.provider)}
                  className="text-xs px-3 py-1.5 text-[#7A3A2A] rounded hover:bg-[#FFF4F0]"
                  data-testid={`key-failure-dismiss-${a.provider}`}
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
